/**
 * Publishing a generated template — the one place the generator persists.
 *
 * Before this existed, "Publish" added the card's id to a React `Set` and did
 * nothing else. The template looked published, and was gone on refresh: it had
 * never left the browser. This sends the compiled Editor Document to the
 * template API the admin screens already use, so a generated design becomes an
 * ordinary template record that the AI review queue lists, the editor opens,
 * and the delete endpoint removes.
 *
 * It deliberately adds no storage of its own. `POST /api/admin/content/templates`
 * is the existing create endpoint, `source: 'ai_generated'` is the existing
 * provenance marker, and `status: 'pending'` is the state the review queue
 * lists first — all three already existed for imported templates.
 */

import { ADMIN_TOKEN_KEY } from '../designV2/openai/openaiSchema';

const TEMPLATES_ENDPOINT = '/api/admin/content/templates';

/** Marks a record as produced by this generator; the review queue filters on it. */
export const AI_SOURCE = 'ai_generated';

/** Review state a published template enters — the queue's default tab. */
export const AI_STATUS = 'pending';

/** Thrown when a template could not be persisted. Carries an admin-safe message. */
export class PublishError extends Error {
  constructor(message, { status = null } = {}) {
    super(message);
    this.name = 'PublishError';
    this.status = status;
  }
}

const readToken = () => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_TOKEN_KEY)) || '';
  } catch {
    return '';
  }
};

const apiBase = () => process.env.REACT_APP_BACKEND_URL || '';

/**
 * An admin-safe message for a failed request.
 *
 * Never surfaces a stack trace or a provider error verbatim: the operator needs
 * to know what to do, and a 500's internals are not that.
 */
const describeFailure = (status, detail) => {
  if (status === 401 || status === 403) return 'Your admin session has expired. Sign in again to publish.';
  if (status === 409) return 'This template has already been published.';
  if (status === 413) return 'The template is too large to save.';
  if (status >= 500) return 'The server could not save this template. Try again in a moment.';
  return detail || `Could not save this template (${status}).`;
};

/**
 * The record body for a compiled result card.
 *
 * `template_json` is the authoritative multi-page document — the editor reads
 * `template_json.pages` and only falls back to the flat `elements` list, whose
 * CanvasElement shape cannot express a V2 element. `elements` is therefore left
 * empty rather than filled with a lossy copy that would quietly disagree with
 * the real design. This mirrors what `templates/import-library` already writes.
 *
 * @param {Object} card   a compiled result card
 * @param {Object} [brief] the wizard brief, for library metadata
 */
export const templateRecordFrom = (card, brief = {}) => {
  const doc = (card && card.editorDocument) || {};
  const tags = [brief.documentType, brief.industry, brief.category, brief.purpose]
    .filter((t) => typeof t === 'string' && t.trim())
    .map((t) => t.trim());

  return {
    name: card.name || brief.campaignTitle || 'AI Template',
    description: card.concept || '',
    canvas_width: doc.canvas_width,
    canvas_height: doc.canvas_height,
    canvas_background: '#ffffff',
    // Intentionally empty — see the note above.
    elements: [],
    template_json: {
      canvas_width: doc.canvas_width,
      canvas_height: doc.canvas_height,
      pages: doc.pages || [],
    },
    tags,
    is_premium: false,
    source: AI_SOURCE,
    status: AI_STATUS,
  };
};

/**
 * Persist one compiled result.
 *
 * @returns {Promise<Object>} the saved template, including its stable `id`
 * @throws {PublishError}
 */
export const publishTemplate = async (card, brief = {}, config = {}) => {
  const { fetchImpl, apiUrl = apiBase(), endpoint = TEMPLATES_ENDPOINT } = config;
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new PublishError('No network client is available.');

  let res;
  try {
    res = await doFetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readToken()}` },
      body: JSON.stringify(templateRecordFrom(card, brief)),
    });
  } catch (err) {
    throw new PublishError(`Could not reach the server: ${err && err.message}`);
  }

  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).detail || ''; } catch { /* keep the status message */ }
    throw new PublishError(describeFailure(res.status, detail), { status: res.status });
  }

  const saved = await res.json();
  if (!saved || !saved.id) throw new PublishError('The server saved the template but returned no id.');
  return saved;
};

/**
 * Remove a template that was persisted. A card that was never published has no
 * server record, so deleting it is a local operation and never calls the API.
 */
export const deletePublishedTemplate = async (templateId, config = {}) => {
  const { fetchImpl, apiUrl = apiBase(), endpoint = TEMPLATES_ENDPOINT } = config;
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new PublishError('No network client is available.');

  let res;
  try {
    res = await doFetch(`${apiUrl}${endpoint}/${templateId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${readToken()}` },
    });
  } catch (err) {
    throw new PublishError(`Could not reach the server: ${err && err.message}`);
  }
  if (!res.ok && res.status !== 404) {
    let detail = '';
    try { detail = (await res.json()).detail || ''; } catch { /* keep the status message */ }
    throw new PublishError(describeFailure(res.status, detail), { status: res.status });
  }
  return true;
};
